import React, {useCallback, useEffect, useRef, useState} from 'react';
import {App, Button, Card, Checkbox, Empty, Modal, Spin, Tabs, Tag} from 'antd';
import {
    DeleteOutlined,
    EditOutlined,
    ExperimentOutlined,
    PauseOutlined,
    PlayCircleOutlined,
    PlusOutlined,
    StopOutlined
} from '@ant-design/icons';
import {useI18n} from '../../../i18n/useI18n';
import {
    deleteDcaPlan,
    getDcaUserConfig,
    listDcaPlans,
    parseDcaSymbolList,
    pauseDcaPlan,
    resumeDcaPlan,
    stopDcaPlan,
} from '../../../services/dcaService';
import type {DcaPlan, DcaPlanDraft, DcaUserConfig} from '../../../types/dca';
import DcaCalculatorCard from './DcaCalculatorCard';
import DcaPlanFormModal from './DcaPlanFormModal';
import DcaPlanDetailDrawer from './DcaPlanDetailDrawer';
import DcaSettingsModal from './DcaSettingsModal';
import {fmtTime} from './dcaConditionUtil';
import './DcaAssistantPage.css';

/** 计划状态徽标配色 */
const STATUS_META: Record<string, { color: string }> = {
    ACTIVE: {color: 'green'},
    PAUSED: {color: 'orange'},
    STOPPED: {color: 'default'},
    COMPLETED: {color: 'blue'},
};

const POLL_INTERVAL_MS = 10000;

/**
 * DCA 助手主页（/tools/dca-assistant）
 * 计划卡片列表（状态/信号/触发统计/操作）+ 创建/编辑弹窗 + 详情抽屉 + 用户设置；
 * 10 秒轮询刷新；暂停/恢复/停止/删除均带确认。
 */
const DcaAssistantPage: React.FC = () => {
    const {t} = useI18n();
    const {message, modal} = App.useApp();

    const [plans, setPlans] = useState<DcaPlan[]>([]);
    const [config, setConfig] = useState<DcaUserConfig | null>(null);
    const [loading, setLoading] = useState(true);
    /** 顶部页签：calculator=计算器，running=运行中的计划 */
    const [activeTab, setActiveTab] = useState<'calculator' | 'running'>('calculator');

    const [formOpen, setFormOpen] = useState(false);
    /** 编辑目标；null=创建 */
    const [editPlan, setEditPlan] = useState<DcaPlan | null>(null);
    /** 创建预填草稿（计算器带入） */
    const [createDraft, setCreateDraft] = useState<DcaPlanDraft | null>(null);
    const [detailPlan, setDetailPlan] = useState<DcaPlan | null>(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    /** 计算器预填（计划卡片「测算」，nonce 触发应用） */
    const [calcPrefill, setCalcPrefill] = useState<{ draft: DcaPlanDraft; nonce: number } | null>(null);
    const prefillNonce = useRef(0);
    /** 恢复前风险确认弹窗目标 */
    const [resumeTarget, setResumeTarget] = useState<DcaPlan | null>(null);
    const [resumeRisk, setResumeRisk] = useState(false);
    const [resuming, setResuming] = useState(false);

    const loadConfig = useCallback(async () => {
        try {
            setConfig(await getDcaUserConfig());
        } catch {
            setConfig(null);
        }
    }, []);

    const loadPlans = useCallback(async () => {
        try {
            setPlans(await listDcaPlans());
        } catch (e: any) {
            message.error(e?.message ?? t('dca.page.loadError'));
        } finally {
            setLoading(false);
        }
    }, [message, t]);

    useEffect(() => {
        void loadPlans();
        void loadConfig();
        const timer = setInterval(() => {
            void loadPlans();
        }, POLL_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [loadPlans, loadConfig]);

    const allowedSymbols = parseDcaSymbolList(config?.allowedSymbols);

    /**
     * 设置弹窗关闭回调：useCallback 稳定引用。
     * 直接传内联箭头会让子组件 useEffect 依赖每次渲染都变，配合外部轮询造成配置被反复拉取。
     */
    const closeSettings = useCallback(() => {
        setSettingsOpen(false);
    }, []);

    const openCreate = () => {
        if (0 === allowedSymbols.length) {
            message.warning(t('dca.page.createNeedSymbols'));
            return;
        }
        setEditPlan(null);
        setCreateDraft(null);
        setFormOpen(true);
    };

    const openCreateFromDraft = (draft: DcaPlanDraft) => {
        setEditPlan(null);
        setCreateDraft(draft);
        setFormOpen(true);
    };

    const openEdit = (plan: DcaPlan) => {
        setCreateDraft(null);
        setEditPlan(plan);
        setFormOpen(true);
    };

    /** 计划「测算」：切到计算器页签、载入配置并自动回测 */
    const openCalculatorForPlan = (plan: DcaPlan) => {
        setActiveTab('calculator');
        setCalcPrefill({
            draft: {
                symbol: plan.symbol,
                timeframe: plan.timeframe,
                monitorFrequency: plan.monitorFrequency,
                lifecycle: plan.lifecycle,
                maxBuyCount: plan.maxBuyCount,
                conditionsJson: plan.conditionsJson,
            },
            nonce: ++prefillNonce.current,
        });
    };

    const handleSaved = (plan: DcaPlan) => {
        void loadPlans();
        // 详情抽屉若正展示同一计划，同步刷新
        setDetailPlan((prev) => (null != prev && prev.id === plan.id ? plan : prev));
    };

    const handlePause = (plan: DcaPlan) => {
        modal.confirm({
            title: t('dca.plan.pause'),
            content: t('dca.plan.pauseConfirm'),
            okText: t('dca.common.ok'),
            cancelText: t('dca.common.cancel'),
            onOk: async () => {
                try {
                    await pauseDcaPlan(plan.id);
                    message.success(t('dca.plan.opOk'));
                    void loadPlans();
                } catch (e: any) {
                    message.error(e?.message ?? t('dca.plan.opFail'));
                }
            },
        });
    };

    const handleStop = (plan: DcaPlan) => {
        modal.confirm({
            title: t('dca.plan.stop'),
            content: t('dca.plan.stopConfirm'),
            okText: t('dca.common.ok'),
            okButtonProps: {danger: true},
            cancelText: t('dca.common.cancel'),
            onOk: async () => {
                try {
                    await stopDcaPlan(plan.id);
                    message.success(t('dca.plan.opOk'));
                    void loadPlans();
                } catch (e: any) {
                    message.error(e?.message ?? t('dca.plan.opFail'));
                }
            },
        });
    };

    const handleDelete = (plan: DcaPlan) => {
        modal.confirm({
            title: t('dca.plan.delete'),
            content: t('dca.plan.deleteConfirm'),
            okText: t('dca.common.ok'),
            okButtonProps: {danger: true},
            cancelText: t('dca.common.cancel'),
            onOk: async () => {
                try {
                    await deleteDcaPlan(plan.id);
                    message.success(t('dca.plan.opOk'));
                    void loadPlans();
                } catch (e: any) {
                    message.error(e?.message ?? t('dca.plan.opFail'));
                }
            },
        });
    };

    const handleResume = (plan: DcaPlan) => {
        // 首次启用未勾选风险确认：先弹风险确认
        if (null == plan.riskConfirmedAt) {
            setResumeTarget(plan);
            setResumeRisk(false);
            return;
        }
        void doResume(plan, false);
    };

    const doResume = async (plan: DcaPlan, riskConfirmed: boolean) => {
        setResuming(true);
        try {
            await resumeDcaPlan(plan.id, riskConfirmed);
            message.success(t('dca.plan.opOk'));
            setResumeTarget(null);
            void loadPlans();
        } catch (e: any) {
            message.error(e?.message ?? t('dca.plan.opFail'));
        } finally {
            setResuming(false);
        }
    };

    const renderCardActions = (plan: DcaPlan) => {
        const actions: React.ReactNode[] = [
            <Button key="detail" type="link" size="small" onClick={() => setDetailPlan(plan)}>
                {t('dca.plan.detail')}
            </Button>,
            <Button key="backtest" type="link" size="small" icon={<ExperimentOutlined/>}
                    onClick={() => openCalculatorForPlan(plan)}>
                {t('dca.plan.backtest')}
            </Button>,
        ];
        if ('ACTIVE' === plan.status || 'PAUSED' === plan.status) {
            actions.push(
                <Button key="edit" type="link" size="small" icon={<EditOutlined/>}
                        onClick={() => openEdit(plan)}>
                    {t('dca.plan.edit')}
                </Button>
            );
        }
        if ('ACTIVE' === plan.status) {
            actions.push(
                <Button key="pause" type="link" size="small" icon={<PauseOutlined/>}
                        onClick={() => handlePause(plan)}>
                    {t('dca.plan.pause')}
                </Button>
            );
        }
        if ('PAUSED' === plan.status) {
            actions.push(
                <Button key="resume" type="link" size="small" icon={<PlayCircleOutlined/>}
                        onClick={() => handleResume(plan)}>
                    {t('dca.plan.resume')}
                </Button>
            );
        }
        if ('ACTIVE' === plan.status || 'PAUSED' === plan.status) {
            actions.push(
                <Button key="stop" type="link" size="small" danger icon={<StopOutlined/>}
                        onClick={() => handleStop(plan)}>
                    {t('dca.plan.stop')}
                </Button>
            );
        }
        if ('STOPPED' === plan.status || 'COMPLETED' === plan.status) {
            actions.push(
                <Button key="delete" type="link" size="small" danger icon={<DeleteOutlined/>}
                        onClick={() => handleDelete(plan)}>
                    {t('dca.plan.delete')}
                </Button>
            );
        }
        return actions;
    };

    const runningPlans = plans.filter((plan) => 'ACTIVE' === plan.status);

    /** 计划卡片网格（全部/运行中共用） */
    const renderPlanGrid = (list: DcaPlan[], emptyText: string) => {
        if (0 === list.length && !loading) {
            return <Empty description={emptyText} className="dca-empty"/>;
        }
        return (
            <div className="dca-grid">
                {list.map((plan) => (
                    <Card key={plan.id} size="small" className="dca-card"
                          actions={renderCardActions(plan)}>
                        <div className="dca-card-head">
                            <span className="dca-card-name" title={plan.planName}>{plan.planName}</span>
                            <Tag color={STATUS_META[plan.status]?.color ?? 'default'}>
                                {t(`dca.plan.status.${plan.status}`)}
                            </Tag>
                        </div>
                        <div className="dca-card-tags">
                            <Tag color="geekblue">{plan.symbol}</Tag>
                            <Tag>{t(`dca.plan.lifecycle.${plan.lifecycle}`)}</Tag>
                            <Tag>{plan.timeframe}</Tag>
                            {null != plan.blockReason ? (
                                <Tag color="red" title={t(`dca.plan.block.${plan.blockReason}`)}>
                                    {t('dca.plan.blockTag')}
                                </Tag>
                            ) : null}
                        </div>
                        <div className="dca-card-meta">
                            <div className="dca-card-meta-item">
                                <span className="dca-card-label">{t('dca.plan.signal')}</span>
                                <Tag color={1 === plan.lastSignalState ? 'green' : 'default'}>
                                    {t(`dca.plan.signal.${plan.lastSignalState}`)}
                                </Tag>
                            </div>
                            <div className="dca-card-meta-item">
                                <span className="dca-card-label">{t('dca.plan.buyProgress')}</span>
                                <span>{t('dca.plan.bought')
                                    .replace('{bought}', String(plan.boughtCount))
                                    .replace('{max}', String(plan.maxBuyCount))}</span>
                            </div>
                        </div>
                        <div className="dca-card-meta">
                            <div className="dca-card-meta-item">
                                <span className="dca-card-label">{t('dca.form.dailyMaxTriggers')}</span>
                                <span>{null == plan.dailyMaxTriggers
                                    ? t('dca.form.unlimited')
                                    : plan.dailyMaxTriggers}</span>
                            </div>
                            <div className="dca-card-meta-item">
                                <span className="dca-card-label">{t('dca.plan.lastChecked')}</span>
                                <span>{null == plan.lastCheckedAt ? t('dca.plan.never') : fmtTime(plan.lastCheckedAt)}</span>
                            </div>
                        </div>
                    </Card>
                ))}
            </div>
        );
    };

    return (
        <div className="dca-page">
            <div className="dca-header">
                <div>
                    <div className="dca-title">{t('dca.page.title')}</div>
                    <div className="dca-subtitle">{t('dca.page.desc')}</div>
                </div>
            </div>

            <Tabs
                activeKey={activeTab}
                className="dca-tabs"
                onChange={(key) => setActiveTab(key as 'calculator' | 'running')}
                items={[
                    {
                        key: 'calculator',
                        label: t('dca.page.tabCalculator'),
                        children: (
                            <div className="dca-tab-pane">
                                <DcaCalculatorCard
                                    symbols={allowedSymbols}
                                    config={config}
                                    prefill={calcPrefill}
                                    onRequestSettings={() => setSettingsOpen(true)}
                                    onRequestCreate={openCreateFromDraft}
                                    onConfigSaved={loadConfig}
                                />

                                <div className="dca-plans-head">
                                    <div className="dca-plans-title">{t('dca.page.myPlans')}</div>
                                    <Button icon={<PlusOutlined/>} onClick={openCreate}>
                                        {t('dca.page.create')}
                                    </Button>
                                </div>
                                <Spin spinning={loading}>
                                    {renderPlanGrid(plans, t('dca.page.empty'))}
                                </Spin>
                            </div>
                        ),
                    },
                    {
                        key: 'running',
                        label: t('dca.page.tabRunning'),
                        children: (
                            <div className="dca-tab-pane">
                                <Spin spinning={loading}>
                                    {renderPlanGrid(runningPlans, t('dca.page.noRunningPlans'))}
                                </Spin>
                            </div>
                        ),
                    },
                ]}
            />

            <DcaPlanFormModal
                open={formOpen}
                plan={editPlan}
                symbols={allowedSymbols}
                draft={createDraft}
                maxPlanBuyCount={config?.maxPlanBuyCount}
                maxPlanDailyTriggers={config?.maxPlanDailyTriggers}
                onClose={() => {
                    setFormOpen(false);
                    setCreateDraft(null);
                }}
                onSaved={handleSaved}
            />
            <DcaPlanDetailDrawer
                plan={detailPlan}
                onClose={() => setDetailPlan(null)}
                onAction={(action, plan) => {
                    if ('pause' === action) {
                        handlePause(plan);
                    } else if ('stop' === action) {
                        handleStop(plan);
                    } else {
                        handleResume(plan);
                    }
                }}
                onEdit={(plan) => {
                    setDetailPlan(null);
                    openEdit(plan);
                }}
            />
            <DcaSettingsModal
                open={settingsOpen}
                onClose={closeSettings}
                onSaved={() => {
                    void loadConfig();
                    void loadPlans();
                }}
            />
            <Modal
                open={null != resumeTarget}
                title={t('dca.resume.title')}
                okText={t('dca.resume.confirm')}
                cancelText={t('dca.common.cancel')}
                confirmLoading={resuming}
                onCancel={() => setResumeTarget(null)}
                onOk={() => {
                    if (null != resumeTarget) {
                        void doResume(resumeTarget, resumeRisk);
                    }
                }}
            >
                <Checkbox checked={resumeRisk} onChange={(e) => setResumeRisk(e.target.checked)}>
                    {t('dca.resume.riskText')}
                </Checkbox>
            </Modal>
        </div>
    );
};

export default DcaAssistantPage;
